import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useEffect, useState } from "react";
import { DabltMngService } from "app/services/mng/DabltMngService";
import { ResponseResultCode } from "app/model/response-api";
import { Constant } from "app/constants/constant";
import CountUp from "react-countup";
import dayjs from "dayjs";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import {numberFormatter, parseNumberWithComma} from "app/utils/stringUtil";
import DashboardLoading from "./DashboardLoading";
import DashboardCard from "./DashboardCard";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
NoDataToDisplay(Highcharts);

const graph: Highcharts.Options = {
  chart: {
    type: 'pie',
    height: 170,
    spacingTop: 0,
    spacingBottom: 0,
    backgroundColor: undefined,
  },
  noData: HighchartsOptionsUtil.noData,
  colors: ['#545CC1', '#4C96F9', '#44C5DB', '#9F92FF', '#FFBB24', '#FD6868', '#F779A6'],
  legend: {
    layout: 'horizontal',
    align: 'right',
    verticalAlign: 'middle',
    alignColumns: true,
    borderWidth: 0,
    symbolWidth: 0,
    symbolHeight: 0,
    symbolPadding: 0,
    itemMarginTop: 5,
    itemMarginBottom: 5,
    width: 350,
    useHTML: true,
    labelFormatter(this: any) {
      return `
      <div style="width: 7rem; display: flex; justify-content: space-between; align-items: center;">
        <span style="font-size: 0.75rem; color: #555;"><span style="color: ${this.color};">●&nbsp;</span>${this.name}</span>
        <span style="font-size: 0.75rem; color: #333; font-weight: 500;">
          ${numberFormatter(this.y)}건
        </span>
      </div>
      `;
    },
  },
  subtitle: {
    x: 0,
    y: 40,
    align: 'center',
    verticalAlign: 'middle',
    useHTML: true,
  },
  series: [{
    type: 'pie',
    data: [
      { name: '과전압', y: 0 },
      { name: '과전류', y: 0 },
      { name: '기타', y: 0 },
    ]
  }],
  tooltip: {
    formatter: function (this: any) {
      return `<span style="color: ${this.color};">●&nbsp;</span>${this.point.name}: <strong style="font-size: 0.75rem;">${this.y}</strong>건`;
    },
    style: {
      fontSize: '0.75rem'
    }
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: "pointer",
      showInLegend: true,
      borderWidth: 0,
      borderRadius: 0,
      innerSize: "60%",
      size: '100%',
      dataLabels: {
        useHTML: true,
        enabled: true,
        distance: -10,
        y: -5,
        formatter(this:any) {
          return (`
            <div style="text-align:center;">
              <span class="bar-graph-color-num" style="position:relative;display:inline-block;margin-top:5px;padding:5px 20px;font-weight:400;border-radius:15px;overflow:hidden;background:${this.point.color};">
                <span style="position: relative; display: block; z-index: 1; color: #fff; font-size: 13px; font-weight: 600;">
                  ${numberFormatter(this.percentage, 1)}%
                </span>
                <em style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, .3);"></em>
              </span>
            </div>
          `);
        },
      },
    },
  },
  responsive: {
    rules: [{
      condition: {
        maxWidth: 500,
      },
      chartOptions: {
        chart: {
          height: 270,
          margin: undefined,
          spacing: [0, 0, 0, 0],
        },
        subtitle: {
          x: 0,
          y: -20,
        },
        noData: HighchartsOptionsUtil.noDataResponsive,
        legend: {
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          width: undefined,
          labelFormatter(this: any) {
            return `
            <div style="width: 5rem; display: flex; justify-content: space-between; align-items: center;">
              <span style="text-align: left; display: flex; align-items: center;">
                <span style="color: ${this.color}; font-size: 0.6rem;">●&nbsp;</span>
                <span style="white-space: initial; font-size: 0.6rem;">
                  ${this.name}
                </span>
              </span>
              <span style="text-align: right; color: #262626; font-size: 0.6rem; font-weight: 500;">
                ${numberFormatter(this.y)}건
              </span>
            </div>
            `;
          },
        },
      }
    }]
  }
};

Highcharts.setOptions({
  ...HighchartsOptionsUtil.locale.kr.globalOptions,
  title: { text: '' },
  exporting: { enabled: false },
  credits: { enabled: false },
  tooltip: { shared: true, headerFormat: '' },
  accessibility: { enabled: false },
});

export default function IssueMng(props: Readonly<DashboardModuleProps>) {
  const [option, setOption] = useState<Highcharts.Options>(graph);
  const [statisticData, setStatisticData] = useState<any>({countDay: 0, countMonth: 0, countYear: 0 });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    DabltMngService.getInstance().getStatisticData().then((res) => {
      if (res && ResponseResultCode.Y === res.resultCode) {
        setStatisticData(res.data)
      }
    });

    const thisYearFirstDay: string = dayjs().startOf('year').format(Constant.FORMAT_YMD);
    DabltMngService.getInstance().pieChartData({startDate: thisYearFirstDay}).then((res) => {
      if (res && ResponseResultCode.Y === res.resultCode) {
        const ovcrn = res.data.ovcrn ?? 0;
        const ovvt = res.data.ovvt ?? 0;
        const othr = res.data.othr ?? 0;
        const total = ovcrn + ovvt + othr;
        const hasData = ovcrn || ovvt || othr ;
        let subtitle = {};
        let seriesData: any[] = [];

        if (hasData) {
          subtitle = {
            x: -183,
            y: 10,
            align: 'center',
            verticalAlign: 'middle',
            text: `
              <div style="display: flex; flex-direction: column; justify-content: center; align-items: center;">
                <div style="color: #555; font-size: 0.7rem; font-weight: 500;">Total</div>
                <div><span style="color: #333; font-size: 0.7rem; font-weight: 700;">${total}</span><span style="color: #444444; font-size: 0.7rem; font-weight: 500;">건</span></span>
              </div>
            `,
          }
        }
        seriesData = [
          { name: '과전압', y: ovcrn },
          { name: '과전류', y: ovvt },
          { name: '기타', y: othr },
        ];
        setOption({
          ...graph,
          subtitle: subtitle,
          series: [{
            type: 'pie',
            data: seriesData,
            borderWidth:(ovcrn === 0 && ovvt === 0 && othr === 0 ) ? 0.5 : 0,
          }],

        })
      }
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  return (
    <DashboardCard
      title="장애 현황"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/mng/mng004"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      <div className="box-graph1 box-style10">
        <div className="box-style10 mgt0">
          <div className="box">
            <div className="inner">
              <p>금년 누적 장애발생 건수</p>
              <strong className="c-num">
                <CountUp
                  end={statisticData.countYear}
                  duration={0.5}
                  formattingFn={(value) => parseNumberWithComma(value)}
                >
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>
                건
              </strong>
            </div>
          </div>
          <div className="box">
            <div className="inner">
              <p>금월 장애발생 건수</p>
              <strong className="c-num">
                <CountUp
                  end={statisticData.countMonth}
                  duration={0.5}
                  formattingFn={(value) => parseNumberWithComma(value)}
                >
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>건
              </strong>
            </div>
          </div>
          <div className="box">
            <div className="inner">
              <p>금일 장애발생 건수</p>
              <strong className="c-num">
                <CountUp
                  end={statisticData.countDay}
                  duration={0.5}
                  formattingFn={(value) => parseNumberWithComma(value)}
                >
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>건
              </strong>
            </div>
          </div>
        </div>
        <div className="graph2">
          <div className="chart">
            {option && (
              <HighchartsReact highcharts={Highcharts} options={option} />
            )}
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}